import {
  type AnalysisDocument,
  BackendError,
  databaseError,
  parseInstagramUrl,
  sortFiles,
  type CompleteResult,
  type AnalysisJob,
  type DepositAnalysis,
  type PublicationStats,
  type DepositBackend,
  type DepositDetail,
  type DepositEventRow,
  type DepositFileRow,
  type DepositManifest,
  type DepositRow,
  type DepositSummary,
  type DuplicateCheck,
  type Member,
  type StartResult,
  type UploadOutcome,
} from "@opulancia/deposit-core";
import type { SupabaseClient } from "@supabase/supabase-js";

const PATH = /^files\/[0-9a-f]{64}$/;
const PAGE = 1000;

interface ApiError {
  code?: string;
  message: string;
  status?: number;
  statusCode?: string | number;
}

/** A PostgREST or Storage error as the interface shows it; a lost connection is retriable. */
export function toBackendError(error: ApiError): BackendError {
  if (!error.code && /fetch|network|load failed|timeout|abort/i.test(error.message)) return new BackendError("network", "Connexion interrompue.");
  return databaseError(error.code, error.message);
}

/**
 * The deposit backend behind Supabase, in the browser, with the publishable key
 * and the member's session. Every rule is enforced by the database and the
 * Storage policies (supabase/migrations/20261009090000_carousel_deposits.sql);
 * this adapter only translates calls. Same contract as the backends tested in
 * packages/deposit-core and apps/deposit-ops.
 */
export class SupabaseDepositBackend implements DepositBackend {
  constructor(private readonly client: SupabaseClient) {}

  private async rpc<T>(name: string, args: Record<string, unknown>): Promise<T> {
    let result;
    try {
      result = await this.client.rpc(name, args);
    } catch (error) {
      throw new BackendError("network", error instanceof Error ? error.message : String(error));
    }
    if (result.error) throw toBackendError(result.error);
    return result.data as T;
  }

  private async rows<T>(query: PromiseLike<{ data: unknown; error: ApiError | null }>): Promise<T[]> {
    let result;
    try {
      result = await query;
    } catch (error) {
      throw new BackendError("network", error instanceof Error ? error.message : String(error));
    }
    if (result.error) throw toBackendError(result.error);
    return (result.data ?? []) as T[];
  }

  async currentMember(): Promise<Member | null> {
    const { data } = await this.client.auth.getUser();
    if (!data.user) return null;
    const rows = await this.rows<Member>(this.client.from("members").select("user_id, display_name").eq("user_id", data.user.id));
    return rows[0] ?? null;
  }

  findDuplicates(zipSha256: string | null, imageSha256: string[]): Promise<DuplicateCheck> {
    return this.rpc("find_duplicates", { p_zip_sha256: zipSha256, p_image_sha256: imageSha256 });
  }

  startDeposit(clientKey: string, manifest: DepositManifest): Promise<StartResult> {
    return this.rpc("start_deposit", { p_client_key: clientKey, p_manifest: manifest });
  }

  async uploadObject(storagePath: string, data: Uint8Array, mediaType: string): Promise<UploadOutcome> {
    if (!PATH.test(storagePath)) throw new BackendError("invalid", "Chemin de stockage refusé.");
    let result;
    try {
      // upsert: false — an existing object is never replaced (and the bucket has no update policy)
      result = await this.client.storage.from("originals").upload(storagePath, new Blob([data as Uint8Array<ArrayBuffer>], { type: mediaType }), {
        contentType: mediaType,
        upsert: false,
        cacheControl: "31536000",
      });
    } catch (error) {
      throw new BackendError("network", error instanceof Error ? error.message : String(error));
    }
    if (!result.error) return "created";
    const error = result.error as ApiError;
    const status = Number(error.status ?? error.statusCode);
    if (status === 409 || /already exists|duplicate/i.test(error.message)) return "exists";
    if (status === 403 || /row-level security|unauthorized/i.test(error.message)) throw new BackendError("not_a_member", "Accès réservé aux membres d'OPULANCIA.");
    if (status === 413 || /too large|exceeded the maximum/i.test(error.message)) throw new BackendError("invalid", "Fichier plus lourd que la limite du stockage.");
    if (status === 415 || /mime type/i.test(error.message)) throw new BackendError("invalid", `Type ${mediaType} refusé par le stockage.`);
    if (!status || status >= 500 || /fetch|network/i.test(error.message)) throw new BackendError("network", error.message);
    throw new BackendError("unknown", error.message);
  }

  completeDeposit(depositId: string): Promise<CompleteResult> {
    return this.rpc("complete_deposit", { p_deposit_id: depositId });
  }

  async listDeposits(): Promise<DepositRow[]> {
    const all: DepositRow[] = [];
    for (let from = 0; ; from += PAGE) {
      const page = await this.rows<DepositRow>(this.client.from("deposits").select("*").order("number", { ascending: false }).range(from, from + PAGE - 1));
      all.push(...page);
      if (page.length < PAGE) return all;
    }
  }

  listMembers(): Promise<Member[]> {
    return this.rows<Member>(this.client.from("members").select("user_id, display_name").order("created_at"));
  }

  async getDeposit(depositId: string): Promise<DepositDetail> {
    const [deposit] = await this.rows<DepositRow>(this.client.from("deposits").select("*").eq("id", depositId));
    if (!deposit) throw databaseError("P0002", "deposit_not_found");
    const files = await this.rows<DepositFileRow>(this.client.from("deposit_files").select("*").eq("deposit_id", depositId));
    const events = await this.rows<DepositEventRow>(this.client.from("deposit_events").select("*").eq("deposit_id", depositId).order("id"));
    return { deposit, files: sortFiles(files), events };
  }

  async getAnalysis(depositId: string): Promise<DepositAnalysis | null> {
    const [row] = await this.rows<DepositAnalysis>(
      this.client.from("deposit_latest_analysis").select("analyzer_version, created_at, current, summary").eq("deposit_id", depositId),
    );
    return row ?? null;
  }

  /** Two reads: which analysis is the latest (the view), then its document (members only, RLS). */
  async getAnalysisDocument(depositId: string): Promise<AnalysisDocument | null> {
    const [latest] = await this.rows<{ analysis_id: string }>(this.client.from("deposit_latest_analysis").select("analysis_id").eq("deposit_id", depositId));
    if (!latest) return null;
    const [row] = await this.rows<{ document: AnalysisDocument }>(this.client.from("deposit_analyses").select("document").eq("id", latest.analysis_id));
    return row?.document ?? null;
  }

  async getPublicationStats(depositId: string): Promise<PublicationStats | null> {
    const [row] = await this.rows<PublicationStats>(
      this.client
        .from("deposit_publication_stats")
        .select("matched, publication_type, published_local, published_at, captured_at, source, age_hours, reach, views, likes, comments, shares, saves, profile_visits, followers_gained")
        .eq("deposit_id", depositId),
    );
    return row ?? null;
  }

  async getAnalysisJob(depositId: string): Promise<AnalysisJob | null> {
    const [row] = await this.rows<AnalysisJob>(
      this.client
        .from("deposit_latest_analysis_job")
        .select("analyzer_version, status, attempts, last_error, partial_summary, lease_until, updated_at, current")
        .eq("deposit_id", depositId),
    );
    return row ?? null;
  }

  async firstSlides(depositIds: string[]): Promise<Map<string, DepositFileRow>> {
    if (!depositIds.length) return new Map();
    const rows = await this.rows<DepositFileRow>(this.client.from("deposit_files").select("*").in("deposit_id", depositIds).eq("role", "slide").eq("position", 1));
    return new Map(rows.map((r) => [r.deposit_id, r]));
  }

  async setSlideOrder(depositId: string, expectedVersion: number, slideIds: string[], extraIds: string[]): Promise<DepositSummary> {
    const result = await this.rpc<{ deposit: DepositSummary }>("set_slide_order", { p_deposit_id: depositId, p_expected_version: expectedVersion, p_slide_ids: slideIds, p_extra_ids: extraIds });
    return result.deposit;
  }

  async linkInstagram(depositId: string, expectedVersion: number, url: string | null, publishedAt: string | null): Promise<DepositSummary> {
    let canonical: string | null = null;
    if (url !== null && url.trim()) {
      try {
        canonical = parseInstagramUrl(url).url;
      } catch {
        throw databaseError("22023", "invalid_link: not an Instagram post URL");
      }
    }
    const result = await this.rpc<{ deposit: DepositSummary }>("link_instagram", { p_deposit_id: depositId, p_expected_version: expectedVersion, p_url: canonical, p_published_at: publishedAt });
    return result.deposit;
  }

  async objectUrl(storagePath: string, downloadName?: string): Promise<string> {
    if (!PATH.test(storagePath)) throw new BackendError("invalid", "Chemin de stockage refusé.");
    const { data, error } = await this.client.storage.from("originals").createSignedUrl(storagePath, 3600, downloadName ? { download: downloadName } : undefined);
    if (error || !data) throw new BackendError("not_found", "Fichier introuvable.");
    return data.signedUrl;
  }
}
